import { Component, effect, inject, signal } from '@angular/core';
import { form, required, FormField, FormRoot } from '@angular/forms/signals';
import { CarePlan } from 'src/app/calvin-emr-shared/models/care-plan.model';
import { MatGridListModule } from '@angular/material/grid-list';
import { ButtonDirective } from 'primeng/button';
import { Timestamp } from '@angular/fire/firestore';
import { PatientDashboardTabComponent } from '../dashboard-tab/dashboard-tab.component';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { PatientService } from 'src/app/calvin-emr-shared/services/patient.service';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';

interface CarePlanModel {
  noticing: string;
  interpreting: string;
  responding: string;
  reflecting: string;
}

@Component({
  selector: 'app-care-plan-tab',
  standalone: true,
  imports: [
    FormField,
    FormRoot,
    MatGridListModule,
    ButtonDirective,
    TooltipModule,
    ToastModule,
  ],
  templateUrl: './care-plan-tab.component.html',
  styleUrl: './care-plan-tab.component.scss',
})
export class CarePlanTabComponent extends PatientDashboardTabComponent {
  private patientService = inject(PatientService);
  private messageService = inject(MessageService);

  carePlan: CarePlan = new CarePlan();
  patient: Patient | undefined;

  private model = signal<CarePlanModel>({
    noticing: '',
    interpreting: '',
    responding: '',
    reflecting: '',
  });

  carePlanForm = form(this.model, (f) => {
    required(f.noticing);
    required(f.interpreting);
    required(f.responding);
    required(f.reflecting);
  });

  constructor() {
    super();
    effect(() => {
      const patient = this.patientStore.patient();
      this.patient = patient;
      this.loadCarePlan();
    });
  }

  private loadCarePlan(): void {
    const carePlanData = (this.patient as any)?.carePlanNotes;
    if (carePlanData) {
      this.carePlan = new CarePlan(
        carePlanData.noticing,
        carePlanData.interpreting,
        carePlanData.responding,
        carePlanData.reflecting,
        carePlanData.lastUpdated
      );
      this.model.set({
        noticing: this.carePlan.noticing || '',
        interpreting: this.carePlan.interpreting || '',
        responding: this.carePlan.responding || '',
        reflecting: this.carePlan.reflecting || '',
      });
    }
  }

  onSave(): void {
    const patientId = this.patient?.id;
    if (!patientId) {
      console.error('Error: Patient ID not available.');
      return;
    }

    const values = this.model();
    this.carePlan.noticing = values.noticing;
    this.carePlan.interpreting = values.interpreting;
    this.carePlan.responding = values.responding;
    this.carePlan.reflecting = values.reflecting;
    this.carePlan.lastUpdated = Timestamp.now();

    this.patientService
      .updatePatientField(patientId, 'carePlanNotes', {
        noticing: this.carePlan.noticing,
        interpreting: this.carePlan.interpreting,
        responding: this.carePlan.responding,
        reflecting: this.carePlan.reflecting,
        lastUpdated: this.carePlan.lastUpdated,
      })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Care plan saved successfully!',
          });
        },
        error: (error: any) => {
          console.error('Error saving care plan notes:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update care plan.',
          });
        },
      });
  }
}
